// import React from 'react';
import { Target, Users, Lightbulb, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { KaryvaLogoAnimated3D } from '../KaryvaLogoAnimated3D';

interface AboutPageProps {
  onNavigate: (page: string) => void;
}

export function AboutPage({ onNavigate }: AboutPageProps) {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl mb-6">Transforming Project Portfolio Management</h1>
              <p className="text-xl text-[#D5D9DD] leading-relaxed mb-8">
                We're building the future of enterprise project management, combining PMI best practices
                with cutting-edge technology to help organizations deliver excellence at scale.
              </p>
            </div>
            <div className="flex justify-center">
              <KaryvaLogoAnimated3D size="hero" darkBg={true} autoPlay={true} />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-[#E8E9E9] rounded-lg p-8 border border-[#C4CDD5]">
              <div className="w-12 h-12 bg-[#DAA520] rounded-lg flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl text-[#2A3B4C] mb-4">Our Mission</h2>
              <p className="text-lg text-[#556B7C] leading-relaxed">
                To empower enterprise organizations with comprehensive, customizable project portfolio
                management solutions that drive measurable success and operational excellence.
              </p>
            </div>

            <div className="bg-[#E8E9E9] rounded-lg p-8 border border-[#C4CDD5]">
              <div className="w-12 h-12 bg-[#DAA520] rounded-lg flex items-center justify-center mb-6">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl text-[#2A3B4C] mb-4">Our Vision</h2>
              <p className="text-lg text-[#556B7C] leading-relaxed">
                To be the global standard for enterprise PPM, recognized for innovation, security,
                and our unwavering commitment to customer success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl text-[#2A3B4C] mb-6">Our Story</h2>
              
              <div className="space-y-6">
                <p className="text-lg text-[#556B7C] leading-relaxed">
                  Karyva was born from a simple observation: enterprise organizations
                  deserve project portfolio management solutions that adapt to their needs, not the other way around.
                </p>
                
                <p className="text-lg text-[#556B7C] leading-relaxed">
                  After witnessing countless organizations struggle with one-size-fits-all SaaS platforms that
                  compromised on security, customization, or both, we set out to create something different.
                  A platform built on PMI/PMBOK principles, deployed in secure single-tenant environments,
                  and fully customizable to each client's unique requirements.
                </p>
                
                <p className="text-lg text-[#556B7C] leading-relaxed">
                  Today, Karyva serves enterprises across financial services, healthcare, technology,
                  manufacturing, and government sectors, helping them achieve measurable improvements in
                  project success rates, governance efficiency, and portfolio visibility.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1601509876296-aba16d4c10a4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBjb2xsYWJvcmF0aW9ufGVufDF8fHx8MTc2NzA3MjkxM3ww&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Team Collaboration"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-white rounded-lg shadow-lg p-4 flex items-center justify-center">
                <KaryvaLogoAnimated3D size="large" darkBg={false} autoPlay={false} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Our Core Values</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ValueCard
              title="Customer First"
              description="Your success is our success. We're committed to delivering solutions that exceed expectations."
            />
            <ValueCard
              title="Innovation"
              description="Continuously evolving our platform with cutting-edge technology and industry best practices."
            />
            <ValueCard
              title="Security"
              description="Uncompromising on data protection with enterprise-grade security in every deployment."
            />
            <ValueCard
              title="Excellence"
              description="Delivering quality in every aspect, from platform capabilities to customer support."
            />
          </div>
        </div>
      </section>

      {/* Why Choose Karyva */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Why Organizations Choose Karyva</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <ReasonCard
              title="Built for Enterprise"
              points={[
                'Single-tenant AWS architecture',
                'Complete data sovereignty',
                'Unlimited scalability',
                'SOC 2 compliance ready',
              ]}
            />
            <ReasonCard
              title="PMI-Aligned"
              points={[
                'Based on PMBOK standards',
                '5-gate approval process',
                '29 compliant templates',
                'Complete governance framework',
              ]}
            />
            <ReasonCard
              title="Fully Customizable"
              points={[
                'Custom workflows & terminology',
                'Configurable approval processes',
                'Branded to your organization',
                'Tailored feature sets',
              ]}
            />
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Our Team</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              A diverse team of project management experts, software engineers, and customer success professionals
              dedicated to your success
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] rounded-lg p-12 text-white text-center">
            <Users className="w-16 h-16 mx-auto mb-6 text-[#DAA520]" />
            <h3 className="text-2xl mb-4">Join Our Growing Team</h3>
            <p className="text-[#D5D9DD] mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals who share our passion for excellence
              in project portfolio management.
            </p>
            <Button
              onClick={() => onNavigate('careers')}
              className="bg-[#DAA520] hover:bg-[#B8860B] text-white"
            >
              View Open Positions
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl text-[#2A3B4C] mb-6">
            Partner With Karyva
          </h2>
          <p className="text-xl text-[#556B7C] mb-8">
            Join leading enterprises who trust Karyva for their project portfolio management needs
          </p>
          <Button
            onClick={() => onNavigate('contact')}
            className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg"
          >
            Contact Karyva Team
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}

function ValueCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="text-center p-6">
      <div className="w-16 h-16 bg-[#DAA520] rounded-full flex items-center justify-center mx-auto mb-4">
        <Shield className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-xl text-[#2A3B4C] mb-3">{title}</h3>
      <p className="text-[#556B7C] leading-relaxed">{description}</p>
    </div>
  );
}

function ReasonCard({ title, points }: { title: string; points: string[] }) {
  return (
    <div className="bg-white rounded-lg p-8 border border-[#C4CDD5]">
      <h3 className="text-xl text-[#2A3B4C] mb-6 pb-4 border-b border-[#C4CDD5]">{title}</h3>
      <ul className="space-y-3">
        {points.map((point, idx) => (
          <li key={idx} className="flex items-start gap-3 text-[#556B7C]">
            <div className="w-5 h-5 bg-[#DAA520] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs">✓</span>
            </div>
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}